/**
 * `derivePrepPhase` — pure function mapping query state →
 * Interview Prep tab render phase.
 *
 * Durable rev (2026-06-17): Interview Prep was re-architected off the SSE
 * stream onto the durable job_queue worker, so this is now poll-driven and
 * mirrors `derivePhase.ts` (the AI-Eval / interview_analysis phaser): a
 * `queued`/`running` row maps to a generating phase. The previous SSE-stream
 * branch (which took precedence over query state) is GONE — there is no
 * stream object anymore.
 *
 * Spec: `frontend/docs/30j_4_design.md` §4.6 (pre-durable);
 *       parity with `lib/derivePhase.ts`.
 *
 * Six phases:
 *   `no-jd`            — !jdId
 *   `loading-existing` — query in flight on tab activation
 *   `empty`            — no row OR row with empty content
 *   `streaming-active` — row.status ∈ {queued, running} (worker generating).
 *                        Name kept for render/test parity; the tab renders the
 *                        buffered skeleton + EvaluationProgressBanner for it.
 *   `loaded`           — row.status='complete' + content non-empty
 *   `error`            — row.status='failed', OR non-404 query error
 *
 * Defensive belts (mirroring `derivePhase.ts`):
 *   - 404 ApiError reaching `query.isError` (hook contract regression)
 *     → `empty`, not error.
 *   - `complete` status with null/whitespace content → `empty`.
 *   - Unknown status enum → fall through to empty.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/lib/apiError";

/**
 * `InterviewPrepRow` — wire shape returned by `GET /interview-prep/{id}`
 * and `GET /interview-prep/by-resource`. Mirrors backend
 * `InterviewPrepReadResponse` (`backend/app/schemas/interview_prep.py`).
 */
export interface InterviewPrepRow {
  id: string;
  candidate_id: string;
  jd_id: string | null;
  status: "queued" | "pending" | "running" | "complete" | "failed";
  content: string | null;
  model_used: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export type PrepPhase =
  | "no-jd"
  | "loading-existing"
  | "empty"
  | "streaming-active"
  | "loaded"
  | "error";

interface DerivePrepPhaseInput {
  jdId: string | undefined;
  query: UseQueryResult<InterviewPrepRow | null, ApiError>;
}

export function derivePrepPhase({
  jdId,
  query,
}: DerivePrepPhaseInput): PrepPhase {
  if (!jdId) return "no-jd";

  // Skeleton until the query SETTLES. Gate on `isPending`
  // (status === "pending" ⇒ no data yet), NOT `isLoading`/`isFetching`.
  // `isLoading` is `isPending && isFetching`, so it is FALSE for a
  // disabled-but-mounted query and for the single render where `enabled`
  // has just flipped true but `fetchStatus` is still "idle". In those
  // frames `data` is still undefined, and the old `isLoading` gate fell
  // through to `"empty"` — flashing the Generate CTA for a frame before an
  // existing prep guide lands. `isPending` holds the skeleton until the
  // query resolves. Parity with `derivePhase.ts` (founder bug FIX 1).
  if (query.isPending) return "loading-existing";

  if (query.isError) {
    // Defensive belt: hook should turn 404 → null, but if a future bug
    // regresses the contract and 404 reaches isError, treat as empty.
    if (query.error instanceof ApiError && query.error.status === 404) {
      return "empty";
    }
    return "error";
  }

  const row = query.data;
  if (!row) return "empty"; // canonical 404-via-null path

  // Worker is generating — the row exists but has no final content yet.
  if (row.status === "queued" || row.status === "running") {
    return "streaming-active";
  }

  if (row.status === "failed") return "error";

  if (row.status === "complete" || row.status === "pending") {
    if (typeof row.content === "string" && row.content.trim().length > 0) {
      return "loaded";
    }
    // Defensive: status implies content, but content is null/empty.
    // Fall through to empty so the user sees the Generate CTA, not a
    // mysterious error surface.
    return "empty";
  }

  // Unknown status enum — defensive fallback.
  return "empty";
}
